import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import AssetPlacesService from "../../../../services/web/asset-places-service.js";

const initialFilter = {
    name: ""
};

const AssetPlacesList = () => {
    const navigate = navigateHook();

    const [filterValues, setFilterValues] = useState(initialFilter);
    const [places, setPlaces] = useState([]);

    const gridRef = useRef(null);
    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const { data, status, isLoading } = useQuery({
        queryKey: ['assetPlaces'],
        queryFn: () => AssetPlacesService.list().then(({ data }) => data ?? [])
    });

    useEffect(() => {
        if (status === 'success') {
            setPlaces(data);
        }
    }, [data, status]);

    const columnDefs = [
        { headerName: "Nome", field: "name", flex: 1, resizable: false, sort: "asc",
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        }
    ];

    const onChange = ({ target: { id, value } }) => {
        setFilterValues(prev => ({ ...prev, [id]: value }));
    };

    const apply = useCallback(() => {
        gridRef.current?.api?.setGridOption(
            "quickFilterText",
            filterValues.name
        );
    }, [filterValues]);

    const clearFilter = useCallback(() => {
        setFilterValues(initialFilter);
        gridRef.current?.api?.setGridOption("quickFilterText", "");
    }, []);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);

    const onCellClicked = ({ data, event }) => {
        const isCtrlPressed = event?.ctrlKey || event?.metaKey;

        const urlDev = `/asset-places/form/${data.id}`;
        
        if (isCtrlPressed) {
            navigate(urlDev, true);
        } else {
            navigate(urlDev);
        }
    };

    const newPlace = () => {
        const urlDev = `/asset-places/form`;
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Locais</Template.Title>
                <Button onClick={newPlace}>
                    <FaPlus /> Adicionar local
                </Button>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <InputForm
                                id="name"
                                label="Nome"
                                value={filterValues.name}
                                onChange={onChange}
                                classNameWrapper="flex-1"
                            />

                            <div className="flex justify-start gap-2">
                                <Button
                                    title="Limpar"
                                    onClick={clearFilter}
                                    target="primary-outline"
                                >
                                    <FaEraser />
                                </Button>

                                <Button title="Buscar" onClick={apply}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className="flex">
                            <div className="h-[calc(100vh-153px)] w-full p-0 ag-theme-quartz">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={places}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus
                                    onCellClicked={onCellClicked}
                                    gridOptions={{ localeText: aggridUtils.localization() }}
                                    onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                                    suppressNoRowsOverlay={isLoading}
                                    theme={theme}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AssetPlacesList;
